import { useEffect, useMemo, useState } from "react";
import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import type { Business } from "@/types/directory";
import RatingStars from "@/components/base/RatingStars";
import SafeImage from "@/components/base/SafeImage";
import { getBusinessFallbackImage } from "@/utils/fallbackImage";

interface PromoBandProps {
  sponsored: Business[];
  featured: Business[];
  loading: boolean;
}

/** How many sponsored rows are visible at once. */
const SPONSORED_VISIBLE = 3;
/** How often the sponsored pool rotates. */
const ROTATE_INTERVAL = 4000;

interface PromoRowProps {
  business: Business;
  variant: "sponsored" | "featured";
}

function PromoRow({ business, variant }: PromoRowProps) {
  const isSponsored = variant === "sponsored";
  const rowClass = isSponsored
    ? "border-[#EBD291] bg-[#FFECB9]"
    : "border-[#A9CBF0] bg-[#CBE3FC]";

  return (
    <article
      className={`animate-promo-row-in group flex flex-col gap-3 rounded-lg border p-3 transition-colors duration-200 sm:flex-row sm:items-center sm:gap-4 ${rowClass}`}
    >
      <Link
        to={`/business/${business.slug}`}
        className="relative block h-20 w-full shrink-0 overflow-hidden rounded-md sm:w-28"
        aria-label={`View ${business.name}`}
      >
        <SafeImage
          src={business.cover}
          fallback={getBusinessFallbackImage(business)}
          alt={`${business.name} - ${business.subcategory}`}
          title={`${business.name} - ${business.subcategory}`}
          className="h-full w-full object-top transition-transform duration-500 group-hover:scale-105"
        />
        <span className="absolute left-1.5 top-1.5 inline-flex items-center gap-1 rounded-full bg-foreground-950/75 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-background-50">
          <i className={`${isSponsored ? "ri-megaphone-line" : "ri-star-smile-line"} text-[11px]`} />
          {isSponsored ? "Sponsored" : "Featured"}
        </span>
      </Link>

      <div className="flex min-w-0 flex-1 flex-col">
        <span className="text-[11px] font-semibold uppercase tracking-wider text-foreground-700">
          {business.subcategory}
        </span>
        <h3 className="mt-0.5 font-heading text-base font-bold leading-snug text-foreground-950">
          <Link to={`/business/${business.slug}`} className="transition-colors hover:text-primary-700">
            {business.name}
          </Link>
        </h3>
        <p className="mt-1 flex items-start gap-1.5 text-xs text-foreground-700">
          <span className="mt-0.5 flex h-3.5 w-3.5 shrink-0 items-center justify-center">
            <i className="ri-map-pin-line text-[13px]" />
          </span>
          {business.address}
        </p>
        <span className="mt-1.5">
          <RatingStars rating={business.rating} count={business.reviewCount} />
        </span>
      </div>

      <div className="flex shrink-0 items-center gap-2 sm:flex-col sm:gap-2">
        <a
          href={`tel:${business.phone.replace(/\s/g, "")}`}
          className="inline-flex flex-1 items-center justify-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-3 py-2 text-xs font-semibold text-background-50 transition-colors duration-200 hover:bg-primary-600 sm:w-28 sm:flex-none"
        >
          <span className="flex h-4 w-4 items-center justify-center">
            <i className="ri-phone-line text-sm" />
          </span>
          Call
        </a>
        <Link
          to={`/business/${business.slug}`}
          className="inline-flex flex-1 items-center justify-center whitespace-nowrap rounded-md border border-foreground-950/20 bg-background-50/70 px-3 py-2 text-xs font-semibold text-foreground-800 transition-colors duration-200 hover:bg-background-50 sm:w-28 sm:flex-none"
        >
          View Details
        </Link>
      </div>
    </article>
  );
}

interface PromoPanelProps {
  title: string;
  subtitle: string;
  icon: string;
  iconClass: string;
  headerClass: string;
  count: number;
  children: ReactNode;
}

function PromoPanel({
  title,
  subtitle,
  icon,
  iconClass,
  headerClass,
  count,
  children,
}: PromoPanelProps) {
  return (
    <div className="rounded-lg border border-background-200 bg-background-50 p-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2.5">
          <span className={`flex h-8 w-8 items-center justify-center rounded-full ${iconClass}`}>
            <i className={`${icon} text-base`} />
          </span>
          <div>
            <h3 className={`font-heading text-sm font-bold ${headerClass}`}>{title}</h3>
            <p className="text-xs text-foreground-500">{subtitle}</p>
          </div>
        </div>
        <span className="rounded-full bg-background-100 px-2.5 py-1 text-[11px] font-bold text-foreground-600">
          {count} live
        </span>
      </div>
      <div className="mt-3 space-y-2.5">{children}</div>
    </div>
  );
}

function PromoAdCta() {
  return (
    <div className="mt-4 flex flex-col items-start gap-4 rounded-lg border border-accent-200 bg-accent-50 p-4 sm:flex-row sm:items-center sm:justify-between md:p-5">
      <div className="flex items-center gap-3">
        <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-accent-500 text-foreground-950">
          <i className="ri-megaphone-line text-xl" />
        </span>
        <div>
          <h3 className="font-heading text-base font-bold text-foreground-950">
            Advertise / Get Featured
          </h3>
          <p className="mt-0.5 max-w-xl text-sm leading-relaxed text-foreground-700">
            Own a business here? Book a sponsored spot or feature your listing to reach thousands of
            local customers searching this district.
          </p>
        </div>
      </div>
      <Link
        to="/listing-plans"
        className="inline-flex w-full items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-3 text-sm font-semibold text-background-50 transition-colors duration-200 hover:bg-primary-600 sm:w-auto"
      >
        <span className="flex h-4 w-4 items-center justify-center">
          <i className="ri-rocket-2-line text-base" />
        </span>
        Get Featured
      </Link>
    </div>
  );
}

export default function PromoBand({ sponsored, featured, loading }: PromoBandProps) {
  const [offset, setOffset] = useState(0);

  useEffect(() => {
    if (sponsored.length <= SPONSORED_VISIBLE) {
      setOffset(0);
      return () => undefined;
    }
    const id = window.setInterval(() => {
      setOffset((prev) => (prev + SPONSORED_VISIBLE) % sponsored.length);
    }, ROTATE_INTERVAL);
    return () => window.clearInterval(id);
  }, [sponsored.length]);

  const visibleSponsored = useMemo(() => {
    if (sponsored.length <= SPONSORED_VISIBLE) return sponsored;
    return Array.from(
      { length: SPONSORED_VISIBLE },
      (_, index) => sponsored[(offset + index) % sponsored.length],
    );
  }, [sponsored, offset]);

  if (loading) {
    return (
      <div className="space-y-4">
        {[0, 1].map((panelIndex) => (
          <div
            key={panelIndex}
            className="rounded-lg border border-background-200 bg-background-50 p-4"
          >
            <div className="h-4 w-32 animate-pulse rounded-full bg-background-200" />
            <div className="mt-4 space-y-3">
              {Array.from({ length: 3 }).map((_, cardIndex) => (
                <div
                  key={cardIndex}
                  className="flex flex-col gap-3 rounded-lg border border-background-200 bg-background-100/60 p-3 sm:flex-row sm:items-center"
                >
                  <div className="h-20 w-full shrink-0 animate-pulse rounded-md bg-background-200 sm:w-28" />
                  <div className="flex-1 space-y-2">
                    <div className="h-3 w-4/5 animate-pulse rounded-full bg-background-200" />
                    <div className="h-3 w-1/2 animate-pulse rounded-full bg-background-200" />
                    <div className="h-3 w-2/5 animate-pulse rounded-full bg-background-200" />
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    );
  }

  return (
    <section aria-label="Sponsored and featured listings" className="space-y-4">
      {sponsored.length > 0 ? (
        <PromoPanel
          title="Sponsored"
          subtitle="Auto-rotating paid placements"
          icon="ri-megaphone-line"
          iconClass="bg-accent-500 text-foreground-950"
          headerClass="text-foreground-950"
          count={sponsored.length}
        >
          {visibleSponsored.map((business) => (
            <PromoRow
              key={`${offset}-${business.id}`}
              business={business}
              variant="sponsored"
            />
          ))}
        </PromoPanel>
      ) : null}

      {featured.length > 0 ? (
        <PromoPanel
          title="Featured Listings"
          subtitle="Hand-picked businesses in this district"
          icon="ri-star-smile-line"
          iconClass="bg-primary-500 text-background-50"
          headerClass="text-foreground-950"
          count={featured.length}
        >
          {featured.map((business) => (
            <PromoRow key={business.id} business={business} variant="featured" />
          ))}
        </PromoPanel>
      ) : null}

      <PromoAdCta />
    </section>
  );
}